import { useEffect, useState } from 'react';

import { Button } from '@/components/Button/Button';

import styles from './NoteEditor.module.css';

interface NoteEditorProps {
  cancelLabel?: string;
  initialValue?: string;
  onCancel: () => void;
  onSave: (note: string) => Promise<void> | void;
  saveLabel?: string;
}

export function NoteEditor({
  cancelLabel = 'Cancel',
  initialValue = '',
  onCancel,
  onSave,
  saveLabel = 'Save note',
}: NoteEditorProps) {
  const [note, setNote] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNote(initialValue);
    setError(null);
  }, [initialValue]);

  async function handleSubmit() {
    const trimmed = note.trim();

    if (!trimmed) {
      setError('Add a note before saving.');
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      await onSave(trimmed);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Unable to save your note right now.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <label className={styles.label}>
        <span className={styles.labelText}>Note</span>
        <textarea
          className={styles.textarea}
          onChange={(event) => setNote(event.target.value)}
          placeholder="What do you want to remember here?"
          rows={4}
          value={note}
        />
      </label>

      {error ? (
        <p aria-live="polite" className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button onClick={onCancel} type="button" variant="secondary">
          {cancelLabel}
        </Button>
        <Button disabled={isSaving} type="submit" variant="primary">
          {isSaving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </form>
  );
}
